<template>
  <div class="v6-root" :class="{ 'v6-still': reduceMotion }">
    <!-- header: title, subtitle, legend — HTML, text tokens only -->
    <div v-if="spec.title || legendItems.length > 1" class="v6-head">
      <div class="v6-titles">
        <span v-if="spec.title" class="v6-title">{{ spec.title }}</span>
        <span v-if="spec.subtitle" class="v6-subtitle">{{ spec.subtitle }}</span>
      </div>
      <div v-if="legendItems.length > 1" class="v6-legend">
        <span v-for="li in legendItems" :key="li.name" class="v6-key" :class="{ dim: li.dim }">
          <span class="v6-swatch" :style="{ background: li.color }" />{{ li.name }}
        </span>
      </div>
    </div>

    <div class="v6-plot" @mouseleave="hover = null">
      <svg :viewBox="`0 0 ${W} ${H}`" preserveAspectRatio="xMidYMid meet">
        <!-- gridlines: hairline, solid, recessive; fade in first -->
        <g v-if="spec.type !== 'donut'" class="v6-grid v6-fade" style="animation-delay: 0ms">
          <line
            v-for="t in yTicks" :key="'g' + t"
            :x1="M.l" :x2="W - M.r" :y1="yScale(t)" :y2="yScale(t)"
          />
        </g>

        <!-- y tick labels -->
        <g v-if="spec.type !== 'donut'" class="v6-ticks v6-fade" style="animation-delay: 80ms">
          <text
            v-for="t in yTicks" :key="'yt' + t"
            :x="M.l - 8" :y="yScale(t) + 3" text-anchor="end"
          >{{ fmt(t) }}</text>
        </g>

        <!-- x axis labels -->
        <g v-if="spec.type !== 'donut'" class="v6-ticks v6-fade" style="animation-delay: 120ms">
          <text
            v-for="(lb, i) in xTickLabels" :key="'xt' + i"
            :x="lb.x" :y="H - M.b + 16" text-anchor="middle"
          >{{ lb.text }}</text>
        </g>

        <!-- goal reference line -->
        <g v-if="spec.goal != null && spec.type !== 'donut'" class="v6-fade" style="animation-delay: 700ms">
          <line class="v6-goal" :x1="M.l" :x2="W - M.r" :y1="yScale(spec.goal)" :y2="yScale(spec.goal)" />
          <text class="v6-goal-label" :x="W - M.r" :y="yScale(spec.goal) - 5" text-anchor="end">
            {{ spec.goalLabel || 'goal' }} {{ fmt(spec.goal) }}
          </text>
        </g>

        <!-- BARS (grouped or stacked): grow from the baseline, staggered -->
        <g v-if="spec.type === 'bar'">
          <rect
            v-for="b in barGeom" :key="b.key"
            class="v6-bar"
            :class="{ dim: b.dim }"
            :x="b.x" :y="b.y" :width="b.w" :height="b.h"
            :fill="b.color"
            :clip-path="b.round ? undefined : 'inset(0)'"
            :rx="b.round ? 4 : 0"
            :style="{ animationDelay: b.delay + 'ms' }"
            @mouseenter="hover = { x: b.cx, y: b.y, lines: b.tip }"
          />
        </g>

        <!-- AREA wash -->
        <g v-if="spec.type === 'area'">
          <path
            v-for="a in areaGeom" :key="a.key"
            class="v6-area v6-fade"
            :d="a.d" :fill="a.color"
            :style="{ animationDelay: a.delay + 'ms' }"
          />
        </g>

        <!-- LINES: draw themselves in via pathLength -->
        <g v-if="spec.type === 'line' || spec.type === 'area'">
          <path
            v-for="l in lineGeom" :key="l.key"
            class="v6-line"
            :class="{ dim: l.dim }"
            :d="l.d" :stroke="l.color" pathLength="1"
            :style="{ animationDelay: l.delay + 'ms' }"
          />
          <!-- markers: surface ring + bloom -->
          <g v-for="l in lineGeom" :key="l.key + 'p'">
            <circle
              v-for="p in l.points" :key="p.key"
              class="v6-dot"
              :class="{ dim: l.dim }"
              :cx="p.x" :cy="p.y" r="4"
              :fill="l.color"
              :style="{ animationDelay: p.delay + 'ms' }"
              @mouseenter="hover = { x: p.x, y: p.y, lines: p.tip }"
            />
          </g>
        </g>

        <!-- SCATTER -->
        <g v-if="spec.type === 'scatter'">
          <circle
            v-for="p in scatterGeom" :key="p.key"
            class="v6-dot"
            :class="{ dim: p.dim }"
            :cx="p.x" :cy="p.y" r="5"
            :fill="p.color"
            :style="{ animationDelay: p.delay + 'ms' }"
            @mouseenter="hover = { x: p.x, y: p.y, lines: p.tip }"
          />
        </g>

        <!-- DONUT: arcs sweep in -->
        <g v-if="spec.type === 'donut'" :transform="`translate(${W / 2}, ${H / 2})`">
          <path
            v-for="a in donutGeom" :key="a.key"
            class="v6-arc v6-fade"
            :d="a.d" :fill="a.color"
            :style="{ animationDelay: a.delay + 'ms' }"
            @mouseenter="hover = { x: W / 2 + a.lx, y: H / 2 + a.ly, lines: a.tip }"
          />
          <text class="v6-donut-total v6-fade" text-anchor="middle" dy="0.1em" style="animation-delay: 600ms">
            {{ fmt(donutTotal) }}
          </text>
          <text
            v-for="a in donutGeom" :key="a.key + 'l'"
            class="v6-ticks v6-fade" text-anchor="middle"
            :x="a.lx * 1.28" :y="a.ly * 1.28"
            :style="{ animationDelay: (a.delay + 250) + 'ms' }"
          >{{ a.label }}</text>
        </g>

        <!-- selective end labels: the last point of each line series -->
        <g v-if="spec.type === 'line'" class="v6-fade" style="animation-delay: 900ms">
          <text
            v-for="l in lineGeom" :key="l.key + 'e'"
            class="v6-endlabel"
            :class="{ dim: l.dim }"
            :x="l.endX + 8" :y="l.endY + 4"
          >{{ fmt(l.endV) }}</text>
        </g>

        <!-- baseline -->
        <line
          v-if="spec.type === 'bar' || spec.type === 'area'"
          class="v6-baseline" :x1="M.l" :x2="W - M.r" :y1="yScale(0)" :y2="yScale(0)"
        />
      </svg>

      <!-- tooltip -->
      <div
        v-if="hover"
        class="v6-tip"
        :style="{ left: (hover.x / W * 100) + '%', top: (hover.y / H * 100) + '%' }"
      >
        <div v-for="(ln, i) in hover.lines" :key="i">{{ ln }}</div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Chart_V6 — the agent-first chart. One declarative spec in, a finished chart
 * out. No colors, no pixels, no d3 in the spec: identity is series ORDER
 * (assigned to the validated categorical palette in fixed order), and every
 * visual decision — mark specs, spacing, grid, labels, animation — is this
 * component's job. Marks emerge from the white surface in a staggered,
 * eased sequence: grid first, then data, then labels.
 *
 * spec = {
 *   type: 'bar' | 'line' | 'area' | 'scatter' | 'donut',
 *   title?, subtitle?,
 *   x: [...labels or numbers],
 *   series: [{ name, values: [...] }],   // values.length === x.length
 *   format?: 'number' | 'percent' | 'dollars',
 *   stacked?: bool (bar only), goal?: number, goalLabel?: string, highlight?: series name,
 * }
 * Validated upstream (the agent runner rejects malformed specs in-loop);
 * this component still degrades gracefully on nulls.
 */
import { scaleBand, scaleLinear, scalePoint } from 'd3-scale';
import { CHART_PALETTE } from '@/utils/chartPalette';
import { line as d3line, area as d3area, arc as d3arc, pie as d3pie, curveMonotoneX } from 'd3-shape';

// The validated categorical order (dataviz reference palette, light mode).
// Fixed assignment by series position — never cycled, never re-ranked.
const PALETTE = CHART_PALETTE;
const DIM = '#d3d2cc';

const W = 720;
const H = 400;
const M = { l: 56, r: 64, t: 16, b: 40 };

export default {
  name: 'Chart_V6',
  props: {
    spec: { type: Object, required: true },
  },
  data() {
    return {
      W, H, M,
      hover: null,
      reduceMotion: typeof window !== 'undefined'
        && window.matchMedia
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    };
  },
  computed: {
    series() {
      return (this.spec.series || []).slice(0, 8).map((s, i) => ({
        name: s.name || `series ${i + 1}`,
        values: (s.values || []).map((v) => (v == null ? null : Number(v))),
        color: this.dimmed(s.name) ? DIM : (s.color || PALETTE[i % 8]),
        dim: this.dimmed(s.name),
      }));
    },
    xVals() { return this.spec.x || []; },
    legendItems() { return this.series.map((s) => ({ name: s.name, color: s.color, dim: s.dim })); },
    numericX() {
      return this.spec.type === 'scatter'
        && this.xVals.every((v) => v !== null && v !== '' && !isNaN(Number(v)));
    },
    xScale() {
      if (this.spec.type === 'bar') {
        return scaleBand().domain(this.xVals.map(String))
          .range([M.l, W - M.r]).paddingInner(0.25).paddingOuter(0.12);
      }
      if (this.numericX) {
        const nums = this.xVals.map(Number);
        return scaleLinear().domain([Math.min(...nums), Math.max(...nums)])
          .nice().range([M.l, W - M.r]);
      }
      return scalePoint().domain(this.xVals.map(String)).range([M.l, W - M.r]).padding(0.5);
    },
    yMax() {
      let m = 0;
      if (this.spec.type === 'bar' && this.spec.stacked) {
        this.xVals.forEach((_, i) => {
          m = Math.max(m, this.series.reduce((a, s) => a + (s.values[i] || 0), 0));
        });
      } else {
        this.series.forEach((s) => s.values.forEach((v) => { if (v != null) m = Math.max(m, v); }));
      }
      if (this.spec.goal != null) m = Math.max(m, this.spec.goal);
      return m || 1;
    },
    yScale() {
      return scaleLinear().domain([0, this.yMax]).nice().range([H - M.b, M.t]);
    },
    yTicks() { return this.yScale.ticks(5); },
    xTickLabels() {
      const xs = this.xVals;
      const maxLabels = 12;
      const step = Math.max(1, Math.ceil(xs.length / maxLabels));
      return xs.map((v, i) => ({ v, i })).filter(({ i }) => i % step === 0)
        .map(({ v }) => ({
          x: this.xCenter(v),
          text: String(v).length > 12 ? `${String(v).slice(0, 11)}…` : String(v),
        }));
    },
    barGeom() {
      if (this.spec.type !== 'bar') return [];
      const out = [];
      const band = this.xScale;
      const stacked = Boolean(this.spec.stacked) && this.series.length > 1;
      const groupW = band.bandwidth();
      const n = this.series.length;
      // ≤24px marks with a 2px surface gap between neighbours/segments.
      const barW = stacked
        ? Math.min(24, groupW)
        : Math.min(24, (groupW - 2 * (n - 1)) / n);
      this.xVals.forEach((xv, xi) => {
        let acc = 0;
        this.series.forEach((s, si) => {
          const v = s.values[xi];
          if (v == null) return;
          const x0 = band(String(xv));
          const x = stacked
            ? x0 + (groupW - barW) / 2
            : x0 + (groupW - (barW * n + 2 * (n - 1))) / 2 + si * (barW + 2);
          const yTop = this.yScale(acc + v);
          const h = Math.max(0, this.yScale(acc) - yTop - (stacked && acc > 0 ? 2 : 0));
          out.push({
            key: `${si}-${xi}`,
            x, y: yTop, w: barW, h,
            cx: x + barW / 2,
            color: s.color, dim: s.dim,
            round: !stacked || si === n - 1,
            delay: 150 + xi * 28 + si * 10,
            tip: [`${xv}`, `${s.name}: ${this.fmt(v)}`],
          });
          if (stacked) acc += v;
        });
      });
      return out;
    },
    lineGeom() {
      if (this.spec.type !== 'line' && this.spec.type !== 'area') return [];
      const gen = d3line()
        .defined((d) => d.v != null)
        .x((d) => d.x).y((d) => this.yScale(d.v))
        .curve(curveMonotoneX);
      return this.series.map((s, si) => {
        const pts = this.xVals.map((xv, i) => ({ x: this.xCenter(xv), v: s.values[i], xv }));
        const def = pts.filter((p) => p.v != null);
        const last = def[def.length - 1] || { x: 0, v: 0 };
        return {
          key: `l${si}`,
          d: gen(pts) || '',
          color: s.color, dim: s.dim,
          delay: 200 + si * 160,
          endX: last.x, endY: this.yScale(last.v), endV: last.v,
          points: def.map((p, pi) => ({
            key: `p${si}-${pi}`, x: p.x, y: this.yScale(p.v),
            delay: 300 + si * 160 + pi * 24,
            tip: [`${p.xv}`, `${s.name}: ${this.fmt(p.v)}`],
          })),
        };
      });
    },
    areaGeom() {
      if (this.spec.type !== 'area') return [];
      const gen = d3area()
        .defined((d) => d.v != null)
        .x((d) => d.x).y0(this.yScale(0)).y1((d) => this.yScale(d.v))
        .curve(curveMonotoneX);
      return this.series.map((s, si) => ({
        key: `a${si}`,
        d: gen(this.xVals.map((xv, i) => ({ x: this.xCenter(xv), v: s.values[i] }))) || '',
        color: s.color,
        delay: 150 + si * 160,
      }));
    },
    scatterGeom() {
      if (this.spec.type !== 'scatter') return [];
      const out = [];
      this.series.forEach((s, si) => {
        this.xVals.forEach((xv, i) => {
          const v = s.values[i];
          if (v == null) return;
          out.push({
            key: `s${si}-${i}`,
            x: this.xCenter(xv), y: this.yScale(v),
            color: s.color, dim: s.dim,
            delay: 150 + i * 14 + si * 60,
            tip: [`${xv}`, `${s.name}: ${this.fmt(v)}`],
          });
        });
      });
      return out;
    },
    donutTotal() {
      const s = this.series[0];
      return s ? s.values.reduce((a, v) => a + (v || 0), 0) : 0;
    },
    donutGeom() {
      if (this.spec.type !== 'donut' || !this.series.length) return [];
      const s = this.series[0];
      const data = this.xVals.map((xv, i) => ({ label: String(xv), v: s.values[i] || 0 }))
        .filter((d) => d.v > 0);
      const R = Math.min(W, H) / 2 - 48;
      const arcs = d3pie().value((d) => d.v).sort(null).padAngle(0.012)(data);
      const gen = d3arc().innerRadius(R * 0.62).outerRadius(R).cornerRadius(3);
      return arcs.map((a, i) => {
        const [lx, ly] = gen.centroid(a);
        return {
          key: `d${i}`,
          d: gen(a),
          color: PALETTE[i % 8],
          lx, ly,
          label: a.data.label,
          delay: 150 + i * 90,
          tip: [a.data.label, `${this.fmt(a.data.v)} (${Math.round(a.data.v / this.donutTotal * 100)}%)`],
        };
      });
    },
  },
  methods: {
    dimmed(name) {
      return Boolean(this.spec.highlight) && this.spec.highlight !== name;
    },
    xCenter(xv) {
      const sc = this.xScale;
      const x = sc(this.numericX ? Number(xv) : String(xv));
      return sc.bandwidth ? x + sc.bandwidth() / 2 : x;
    },
    fmt(v) {
      if (v == null) return '—';
      const f = this.spec.format;
      if (f === 'percent') return `${Number(v) % 1 === 0 ? v : Number(v).toFixed(1)}%`;
      const abs = Math.abs(v);
      const compact = abs >= 1e6 ? `${(v / 1e6).toFixed(1)}M`
        : abs >= 1e4 ? `${(v / 1e3).toFixed(0)}K`
          : Number(v) % 1 === 0 ? Number(v).toLocaleString() : Number(v).toFixed(1);
      return f === 'dollars' ? `$${compact}` : compact;
    },
  },
};
</script>

<style scoped>
.v6-root {
  --surface: #ffffff;
  --grid: #f0efec;
  --ink: #0b0b0b;
  --ink-2: #52514e;
  --ink-3: #a3a29b;
  background: var(--surface);
  font-family: inherit;
}
.v6-head { display: flex; align-items: baseline; gap: 16px; padding: 2px 4px 10px; flex-wrap: wrap; }
.v6-titles { display: flex; flex-direction: column; }
.v6-title { font-size: 14px; font-weight: 600; color: var(--ink); }
.v6-subtitle { font-size: 12px; color: var(--ink-2); }
.v6-legend { display: flex; gap: 14px; margin-left: auto; flex-wrap: wrap; }
.v6-key { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; color: var(--ink-2); }
.v6-key.dim { color: var(--ink-3); }
.v6-swatch { width: 9px; height: 9px; border-radius: 50%; display: inline-block; }

.v6-plot { position: relative; }
.v6-plot svg { display: block; width: 100%; height: auto; }

.v6-grid line { stroke: var(--grid); stroke-width: 1; }
.v6-baseline { stroke: #dddcd6; stroke-width: 1; }
.v6-ticks text { font-size: 11px; fill: var(--ink-3); font-variant-numeric: tabular-nums; }
.v6-endlabel { font-size: 11px; fill: var(--ink-2); font-variant-numeric: tabular-nums; }
.v6-endlabel.dim { fill: var(--ink-3); }
.v6-goal { stroke: var(--ink-3); stroke-width: 1; stroke-dasharray: 5 4; }
.v6-goal-label { font-size: 10px; fill: var(--ink-3); }
.v6-donut-total { font-size: 26px; font-weight: 600; fill: var(--ink); }

/* ————— emergence: grid, then data grows out of the surface, then labels ———— */
.v6-fade { opacity: 0; animation: v6fade 0.5s ease-out forwards; }
@keyframes v6fade { to { opacity: 1; } }

.v6-bar {
  transform-box: fill-box;
  transform-origin: bottom;
  transform: scaleY(0);
  animation: v6grow 0.6s cubic-bezier(0.2, 0.7, 0.3, 1) forwards;
}
@keyframes v6grow { to { transform: scaleY(1); } }

.v6-line {
  fill: none;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dasharray: 1;
  stroke-dashoffset: 1;
  animation: v6draw 0.9s cubic-bezier(0.3, 0.6, 0.3, 1) forwards;
}
@keyframes v6draw { to { stroke-dashoffset: 0; } }

.v6-dot {
  stroke: var(--surface);
  stroke-width: 2;               /* the surface ring */
  transform-box: fill-box;
  transform-origin: center;
  transform: scale(0);
  animation: v6bloom 0.45s cubic-bezier(0.2, 0.9, 0.4, 1.25) forwards;
}
@keyframes v6bloom { to { transform: scale(1); } }

.v6-area { opacity: 0; fill-opacity: 0.1; animation: v6fade 0.8s ease-out forwards; }
.v6-arc { stroke: var(--surface); stroke-width: 2; }

.dim { transition: opacity 0.2s; }

.v6-tip {
  position: absolute;
  transform: translate(-50%, -120%);
  background: var(--ink);
  color: #fff;
  font-size: 11px;
  line-height: 1.5;
  border-radius: 6px;
  padding: 5px 9px;
  pointer-events: none;
  white-space: nowrap;
  z-index: 5;
}

/* respect reduced motion: everything just appears */
.v6-still .v6-fade, .v6-still .v6-bar, .v6-still .v6-line, .v6-still .v6-dot, .v6-still .v6-area {
  animation: none;
  opacity: 1;
  transform: none;
  stroke-dashoffset: 0;
}
</style>
